import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNowStrict } from "date-fns";
import { es } from "date-fns/locale";
import { RefreshCw, Flame, Calendar, X } from "lucide-react";

import AdminLayout from "@/components/admin/AdminLayout";
import { Badge } from "./AdminDashboard";
import { pipeline, patchStatus, loseLead, lossReasons, rescoreAll, SITIO_BADGE, TEMP_BADGE } from "@/lib/admin-api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COLUMN_META = {
    nuevo: { label: "Nuevo", color: "bg-neutral-100" },
    contactado: { label: "Contactado", color: "bg-sky-100" },
    seguimiento: { label: "Seguimiento", color: "bg-blue-100" },
    cotizacion_enviada: { label: "Cotización enviada", color: "bg-purple-100" },
    visita_agendada: { label: "Visita agendada", color: "bg-cyan-100" },
    negociacion: { label: "Negociación", color: "bg-yellow-100" },
    anticipo_recibido: { label: "Anticipo recibido", color: "bg-orange-100" },
    ganado: { label: "Ganado", color: "bg-green-100" },
    perdido: { label: "Perdido", color: "bg-red-100" },
};
const COL_ORDER = Object.keys(COLUMN_META);

export default function AdminKanban() {
    const navigate = useNavigate();
    const [board, setBoard] = useState(null);
    const [filters, setFilters] = useState({ sitio: "", responsable: "" });
    const [loseModal, setLoseModal] = useState(null); // { leadId, fromCol, reasons }
    const [loseReason, setLoseReason] = useState("");
    const [loseNote, setLoseNote] = useState("");
    const [allReasons, setAllReasons] = useState([]);

    const load = () => {
        const params = {};
        if (filters.sitio) params.sitio = filters.sitio;
        if (filters.responsable) params.responsable = filters.responsable;
        pipeline(params).then(setBoard).catch(() => toast.error("Error cargando pipeline"));
    };

    useEffect(() => { load(); }, [filters.sitio, filters.responsable]);
    useEffect(() => { lossReasons().then(setAllReasons); }, []);

    const onDragEnd = async (result) => {
        const { destination, source, draggableId } = result;
        if (!destination || destination.droppableId === source.droppableId) return;
        const newStatus = destination.droppableId;
        const oldStatus = source.droppableId;

        if (newStatus === "perdido") {
            setLoseModal({ leadId: draggableId, fromCol: oldStatus });
            setLoseReason("");
            setLoseNote("");
            return;
        }

        // optimistic
        const fromList = [...board.columns[oldStatus]];
        const [moved] = fromList.splice(source.index, 1);
        const toList = [...board.columns[newStatus]];
        toList.splice(destination.index, 0, moved);
        setBoard({
            ...board,
            columns: { ...board.columns, [oldStatus]: fromList, [newStatus]: toList },
            counts: { ...board.counts, [oldStatus]: fromList.length, [newStatus]: toList.length },
        });

        try {
            await patchStatus(draggableId, newStatus);
            toast.success(`${COLUMN_META[oldStatus].label} → ${COLUMN_META[newStatus].label}`);
        } catch (e) {
            toast.error("Falló el cambio de estatus");
            load();
        }
    };

    const confirmLose = async () => {
        if (!loseReason) return toast.error("Selecciona un motivo");
        try {
            await loseLead(loseModal.leadId, loseReason, loseNote || null);
            toast.success("Lead marcado como perdido");
            setLoseModal(null);
            load();
        } catch { toast.error("Error al marcar perdido"); }
    };

    const onRescore = async () => {
        try { const r = await rescoreAll(); toast.success(`${r.updated_count} actualizados`); load(); }
        catch { toast.error("Falló rescore"); }
    };

    if (!board) return <AdminLayout title="Pipeline"><div className="text-eclipse-muted">Cargando...</div></AdminLayout>;

    return (
        <AdminLayout title="Pipeline">
            <div className="flex flex-wrap items-end gap-3 mb-5">
                <div>
                    <Label className="text-xs font-display font-semibold text-eclipse-muted uppercase mb-1 block">Sitio</Label>
                    <Select value={filters.sitio || "all"} onValueChange={(v) => setFilters({ ...filters, sitio: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-40" data-testid="pipeline-filter-sitio"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="eclipse">Eclipse</SelectItem>
                            <SelectItem value="flexshade">FlexShade</SelectItem>
                            <SelectItem value="velarias">Velarias</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs font-display font-semibold text-eclipse-muted uppercase mb-1 block">Responsable</Label>
                    <Input value={filters.responsable} onChange={(e) => setFilters({ ...filters, responsable: e.target.value })} placeholder="Nombre" className="w-48" data-testid="pipeline-filter-responsable" />
                </div>
                {(filters.sitio || filters.responsable) && (
                    <Button variant="outline" size="sm" onClick={() => setFilters({ sitio: "", responsable: "" })} data-testid="pipeline-clear-filters">Limpiar</Button>
                )}
                <div className="ml-auto">
                    <Button onClick={onRescore} className="btn-eclipse-outline-dark text-xs px-4 py-2 rounded-sm" data-testid="pipeline-rescore-btn">
                        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Recalcular scores
                    </Button>
                </div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
                    {COL_ORDER.map((col) => (
                        <Droppable key={col} droppableId={col}>
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} data-testid={`column-${col}`}
                                    className={`flex-shrink-0 w-72 rounded-sm border border-neutral-200 ${COLUMN_META[col].color} ${snapshot.isDraggingOver ? "ring-2 ring-eclipse-cta" : ""}`}>
                                    <div className="px-3 py-2.5 border-b border-neutral-200 bg-white/60 flex items-center justify-between">
                                        <span className="font-display font-bold text-xs uppercase tracking-wider text-eclipse-primary">{COLUMN_META[col].label}</span>
                                        <span className="text-[11px] font-bold text-eclipse-muted bg-white px-2 py-0.5 rounded-full">{board.counts[col] || 0}</span>
                                    </div>
                                    <div className="p-2 space-y-2 min-h-[140px] max-h-[calc(100vh-280px)] overflow-y-auto">
                                        {(board.columns[col] || []).map((lead, idx) => (
                                            <Draggable key={lead.id} draggableId={lead.id} index={idx}>
                                                {(prov, snap) => (
                                                    <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                                        onClick={() => navigate(`/admin/leads/${lead.id}`)}
                                                        data-testid={`kanban-card-${lead.id}`}
                                                        className={`bg-white rounded-sm border p-3 cursor-pointer hover:border-eclipse-cta hover:shadow ${snap.isDragging ? "rotate-2 shadow-2xl border-eclipse-cta" : "border-neutral-200"}`}>
                                                        <div className="flex items-center justify-between mb-1.5">
                                                            <Badge {...TEMP_BADGE[lead.lead_temp]} bordered />
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="font-display font-black text-eclipse-primary text-sm">{lead.lead_score}</span>
                                                                <Badge {...SITIO_BADGE[lead.sitio]} />
                                                            </div>
                                                        </div>
                                                        <p className="font-display font-bold text-sm text-eclipse-primary leading-tight mb-1 truncate">{lead.nombre}</p>
                                                        <p className="text-[11px] text-eclipse-muted truncate">{lead.empresa || lead.telefono}</p>
                                                        <p className="text-[11px] text-eclipse-muted truncate">{lead.tipo_espacio}{lead.ciudad ? " · " + lead.ciudad : ""}</p>
                                                        <div className="flex items-center justify-between mt-2 text-[10px] text-eclipse-muted">
                                                            <span>{lead.created_at && formatDistanceToNowStrict(parseISO(lead.created_at), { locale: es }) + " atrás"}</span>
                                                            {lead.responsable && <span className="font-bold text-eclipse-primary">{lead.responsable.split(" ").map((s) => s[0]).join("")}</span>}
                                                        </div>
                                                        {lead.next_followup_at && (
                                                            <div className="mt-2 inline-flex items-center gap-1 text-[10px] bg-eclipse-cta/15 text-eclipse-cta px-1.5 py-0.5 rounded font-bold">
                                                                <Calendar className="w-3 h-3" /> Seguir: {format(parseISO(lead.next_followup_at), "d MMM", { locale: es })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    ))}
                </div>
            </DragDropContext>

            <Dialog open={!!loseModal} onOpenChange={(o) => !o && setLoseModal(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><X className="w-5 h-5 text-red-600" /> Marcar como perdido</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-display font-semibold uppercase mb-1.5 block">Motivo *</Label>
                            <Select value={loseReason} onValueChange={setLoseReason}>
                                <SelectTrigger data-testid="lose-modal-reason"><SelectValue placeholder="Selecciona motivo" /></SelectTrigger>
                                <SelectContent>{allReasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-display font-semibold uppercase mb-1.5 block">Nota interna (opcional)</Label>
                            <Textarea value={loseNote} onChange={(e) => setLoseNote(e.target.value)} rows={2} data-testid="lose-modal-note" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLoseModal(null)}>Cancelar</Button>
                        <Button onClick={confirmLose} data-testid="lose-modal-confirm" className="bg-red-600 hover:bg-red-700 text-white">Confirmar pérdida</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
